<template>
  <Card :dockedPosition="dockedPosition" :footerDivider="false" :headerDivider="false">
    <template v-slot:title>
      <FlexGrid id="vehicle-data-header">
        <template v-slot:flex-left>
          <h2 class="headline">
            {{ $t('change.title') }}
          </h2>
        </template>
        <template v-slot:flex-right>
          <div id="edit-link" @click="editVehicle">
            <a class="filter-headline">Anpassen</a>
            <span class="icon icon-filter open-filter-icon"></span>
          </div>
        </template>
      </FlexGrid>
    </template>
    <template v-slot:content>
      <DataBox>
        <template v-slot:databoxContent>
          <DataSection title="">
            <template v-slot:dataLines>
              <DataLine
                id="vehicle-model"
                :label="$t('change.search.myNewCar')"
                :value="getVehicleModel()"
              ></DataLine>
              <DataLine
                id="first-registration"
                :label="$t('change.search.firstRegistration')"
                :value="formatDate(changeVehicleSearch.vehicleFirstRegistration)"
              ></DataLine>
              <DataLine
                id="first-registration-on-me"
                :label="$t('change.search.firstRegistrationOnMe')"
                :value="
                  formatDate(changeVehicleSearch.vehicleFirstRegistrationForPartner)
                "
              ></DataLine>
              <DataLine
                id="mileage"
                :label="$t('change.search.mileage')"
                :value="formatKilometers(changeVehicleSearch.mileageAtTheBeginning)"
              ></DataLine>
              <DataLine
                id="insurance-begin"
                :label="$t('change.search.insuranceBegin')"
                :value="formatDate(changeVehicleSearch.desiredStartDate)"
              ></DataLine>
              <DataLine
                id="drivers-list"
                :label="$t('change.search.userGroup.whoDrivesMyCar')"
                :value="getDrivers()"
              ></DataLine>
              <DataLine
                v-if="changeVehicleSearch.birthyearPartner"
                id="birthyear-partner"
                :label="$t('change.search.userGroup.birthyearPartner')"
                :value="changeVehicleSearch.birthyearPartner"
              ></DataLine>
              <DataLine
                v-if="changeVehicleSearch.birthyearOldestDriver"
                id="birthyear-oldest-driver"
                :label="$t('change.search.userGroup.birthyearOldest')"
                :value="changeVehicleSearch.birthyearOldestDriver"
              ></DataLine>
              <DataLine
                v-if="changeVehicleSearch.birthyearYoungestDriver"
                id="birthyear-youngest-driver"
                :label="$t('change.search.userGroup.birthyearYoungest')"
                :value="changeVehicleSearch.birthyearYoungestDriver"
              ></DataLine>
            </template>
            <!--slot:databoxcontent-->
          </DataSection>
        </template>
      </DataBox>
    </template>
  </Card>
</template>

<script setup lang="ts">
import { type PropType } from 'vue'
import { Card, DataBox, DataLine, DataSection, FlexGrid } from 'de-zurich-ui-library'
import { formatDate } from '@/utils/policy/PolicyUtils'
import { formatKilometers } from '@/utils/motor/MotorDataBoxDataUtils'
import { type ChangeVehicleSearch } from '@/typesTenant/VehicleSelectionApi'
import { t } from '@/i18n'

const props = defineProps({
  dockedPosition: {
    type: String,
    default: 'false'
  },
  changeVehicleSearch: {
    type: Object as PropType<ChangeVehicleSearch>,
    required: true
  }
})

const emit = defineEmits<{
  'edit-vehicle': [none: void]
}>()

function getDrivers(): string {
  let ret: string = t('change.search.userGroup.policyHolder') as string
  if (props.changeVehicleSearch.birthyearPartner) {
    ret += '\n' + t('change.search.userGroup.partner')
  }
  if (props.changeVehicleSearch.birthyearYoungestDriver) {
    ret += '\n' + t('change.search.userGroup.others')
  }
  return ret
}

function getVehicleModel(): string {
  return t('change.search.queryResult.unformatted', {
    manufacturer: props.changeVehicleSearch?.vehicleSearchResult?.manufacturer,
    modelType: props.changeVehicleSearch?.vehicleSearchResult?.modelType,
    kilowatt: props.changeVehicleSearch?.vehicleSearchResult?.kilowatt
  }) as string
}

function editVehicle() {
  emit('edit-vehicle')
}
</script>

<style scoped lang="scss">
:deep(.flex-footer) {
  flex-direction: row !important;
  align-items: center;
}

:deep(.flex-footer__left, .flex-footer__right) {
  flex-grow: 1;
}

.filter-headline {
  font-size: var(--th-fontsize);
  font-family: var(--tr-highlighted-font-family) !important;
  color: var(--link-color) #0071c8;
  margin-right: 16px;
  vertical-align: sub;
}

.open-filter-icon {
  cursor: pointer;
  border-radius: 50%;
  padding: 3px;
  font-size: 20px;
  background-color: var(--link-color);
  color: white;
  float: right;
  margin-top: 4px;

  &:hover {
    background-color: var(--link-color-hover);
  }
}

#vehicle-data-header .flex-footer {
  margin-bottom: 0;
  /*
       Mobile view of headline should not make a line break between headline text and edit link
       to achieve this, the --xs style must be the same as the others (--sm ... --lg).
       This is a workaround and should be solved in the ui library later.
   */
  display: flex;
  flex-direction: column-reverse;

  .cta-link {
    @extend .cta-link;
  }

  &--xs {
    .headline {
      padding: 0;
    }
  }

  &--xs,
  &--sm,
  &--md,
  &--lg {
    flex-direction: row;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    width: 100%;

    .cta-link {
      flex-grow: 1;
      margin-right: var(--grid-gutter-width);
    }

    .button {
      margin-right: var(--flex-footer-button-m-r);
      width: auto;
      &:last-child {
        margin-right: 0;
      }
    }
    .button--cta-link {
      display: inline;
      width: auto;
      flex-grow: 1;
      border: none;
    }
    button ~ button ~ .button--cta-link {
      display: block;
      margin-top: var(--card-footer-left-button-mt);
    }

    .flex-footer__left {
      flex-grow: 1;
      margin-right: var(--grid-gutter-width);
    }

    .flex-footer__right {
      flex-grow: 1;
      text-align: right;
    }
  }
}

#vehicle-data-footer {
  margin-bottom: var(--baseline);
}
</style>
